import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';

import { getValuesForSelectedOptions } from 'commons/productUtils';
import { ADD_TO_RECENTLY_VIEWED } from 'mocks/mutations';
import {
  addToRecentlyViewed,
  getRecentlyViewed,
} from 'components/consumer/cart/utils';
import { setPalomaPixelData } from 'commons/localStorage';
import { logError } from 'commons/logger';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePdpLastSelectedOptionId from 'components/consumer/PDP/state/pdpLastSelectedOptionId/usePdpLastSelectedOptionId';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from 'components/consumer/PDP/state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import usePDPClearanceData from 'data-hooks/useClearanceData/usePDPClearanceData';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import usePDPPrice from 'data-hooks/usePDPPrice';
import usePDPContext from '../../components/consumer/PDP/state/context/usePDPContext';
import {
  FB_EVENTS,
  sendFBEvent,
  sendCustomFBEvent,
} from '../../commons/fbEvents';
import avo from '../../analytics/avo';
import useUserData from '../useUserData';
import { getGTMAddToCartData } from './useUserCartEvent';
import useProductFavoriteV2 from '../useProductFavoriteV2';

import {
  WOOD_PRODUCT_OPTION_ID,
  FABRIC_PRODUCT_OPTION_ID,
  LEATHER_PRODUCT_OPTION_ID,
  WOOD_STAIN_PRODUCT_OPTION_ID,
  VENDOR_LEATHER_PRODUCT_OPTION_ID,
} from '../../commons/constants';

/// /////////////////////
// VIEW PDP
/// /////////////////////

const recentlyViewedEvent = ({ product, saveRecentlyViewed }) => {
  const recentlyViewedIdsList = getRecentlyViewed();
  if (recentlyViewedIdsList[0] !== product.id.toString()) {
    addToRecentlyViewed([product.id.toString()]);
    saveRecentlyViewed({
      variables: {
        productIds: [product.id.toString()],
      },
    });
  }
};

export const viewPDPEvent = ({
  isClearance = false,
  isDecideLater = false,
  isFavorited = false,
  isFlashSale = false,
  isQuickShip = false,
  optionPrice,
  options,
  price,
  product,
  saveRecentlyViewed,
  selectedOptions,
  sku,
  userEmail,
  userId,
}) => {
  if (!product?.sku?.split('-')?.[0]) {
    logError(
      'FB_ERROR: Missing content_id in Facebook events',
      { fbError: true },
      {
        event: FB_EVENTS.VIEW_PDP,
        isClearance,
        isDecideLater,
        isFavorited,
        isFlashSale,
        isQuickShip,
        optionPrice,
        options: JSON.stringify(options),
        price,
        product: JSON.stringify(product),
        selectedOptions: JSON.stringify(selectedOptions),
        sku,
        userEmail,
        userId,
      }
    );
  }

  sendFBEvent(FB_EVENTS.VIEW_PDP, {
    content_ids: [product.sku.split('-')[0]],
    content_name: product.name,
    content_type: 'product',
  });

  const variantSelectedOptions = getValuesForSelectedOptions(
    options,
    selectedOptions
  );

  const eventData = getGTMAddToCartData({
    sku,
    product,
    price,
    optionPrice,
    options,
    selectedOptions,
    isDecideLater,
    isFavorited,
    isQuickShip,
    isClearance,
  });

  const productViewed = {
    productId: product.id,
    category: product.type.name,
    isClearance,
    name: product.name,
    price: parseFloat(price?.final_price ?? 0),
    // quantity: parseInt(find(product.attributes, { code: 'qty' })?.value, 10),
    quantity: 1,
    sku: eventData.sku,
    variant: Object.keys(variantSelectedOptions)
      .map(e =>
        variantSelectedOptions[e]?.value
          ? variantSelectedOptions[e].value
          : null
      )
      .filter(Boolean)
      .join(', '),
    fullPrice: parseFloat(price.original_price ?? 0),
    productFamily: product?.family?.name || '',
    contentIds: [product.sku.split('-')[0]],
    currency: 'USD',
    isFlashSale,
  };
  avo.productViewed(productViewed);

  recentlyViewedEvent({ product, saveRecentlyViewed });
  setPalomaPixelData();
};

/// /////////////////////
// SELECT VARIANT
/// /////////////////////
const getSelectedVariantData = ({
  sku,
  product,
  price,
  optionPrice,
  options,
  selectedOptions,
  isDecideLater = false,
  pdpLastSelectedOptionId,
}) => {
  const result = {};

  result.price = (price?.final_price || 0) + (optionPrice || 0);
  result.full_price = (price?.original_price || 0) + (optionPrice || 0);
  result.exposed_wood = product.exp_wood_product;
  result.product_family = product.family && product.family.name;
  result.product_type = product.type.name;
  result.product_id = product.id;
  result.product_name = product.name;
  result.product_source = product.efg_flag === 0 ? 'internal' : 'external';
  result.reviews = product.meta.reviews.count;
  result.sku = product.sku + sku;

  const { fabric, leather, woodStain } = getValuesForSelectedOptions(
    options,
    selectedOptions
  );
  if (fabric && isDecideLater) {
    result.fabric_color = 'Decide Later';
    result.fabric_id = 0;
    result.fabric_name = 'Decide Later';
  } else if (leather && isDecideLater) {
    result.fabric_color = 'Decide Later';
    result.fabric_id = 0;
    result.fabric_name = 'Decide Later';
  } else if (pdpLastSelectedOptionId === FABRIC_PRODUCT_OPTION_ID) {
    // if fabric changed - send only fabric data.
    result.fabric_color = fabric ? fabric.color : '';
    result.fabric_id = fabric ? fabric.id : 0;
    result.fabric_name = fabric ? fabric.value : '';
  } else if (
    pdpLastSelectedOptionId === LEATHER_PRODUCT_OPTION_ID ||
    pdpLastSelectedOptionId === VENDOR_LEATHER_PRODUCT_OPTION_ID
  ) {
    // if fabric changed - send only fabric data.
    result.fabric_color = leather ? leather.color : '';
    result.fabric_id = leather ? leather.id : 0;
    result.fabric_name = leather ? leather.value : '';
  } else if (
    pdpLastSelectedOptionId === WOOD_STAIN_PRODUCT_OPTION_ID ||
    pdpLastSelectedOptionId === WOOD_PRODUCT_OPTION_ID
  ) {
    // if wood changed - send only wood data
    result.wood_stain_id = woodStain ? woodStain.id : 0;
    result.wood_stain = woodStain ? woodStain.value : '';
  }
  return result;
};

export const selectVariantAnalyticsEvent = ({
  sku,
  product,
  price,
  optionPrice,
  options,
  selectedOptions,
  isDecideLater,
  pdpLastSelectedOptionId,
}) => {
  const data = getSelectedVariantData({
    sku,
    product,
    price,
    optionPrice,
    options,
    selectedOptions,
    isDecideLater,
    pdpLastSelectedOptionId,
  });

  sendCustomFBEvent(FB_EVENTS.SELECT_VARIATION, {
    content_category: data.productType,
    content_ids: [product.sku],
    content_name: data.productName,
    content_type: 'product',
  });

  avo.variationSelected({
    contentIds: [product.sku.split('-')[0]],
    fabricColor: data?.fabric_color ? data?.fabric_color : '',
    fabricId: data?.fabric_id ? data?.fabric_id : 0,
    fabricName: data?.fabric_name ? data?.fabric_name : '',
    fullPrice: parseFloat(data.full_price),
    price: parseFloat(data.price),
    productFamily: product?.family?.name || '',
    productId: +product.id,
    name: product.name,
    sku: data.sku,
    currency: 'USD',
  });
};

const usePDPBasicEvents = () => {
  // effects
  const { email: userEmail, userId } = useUserData();
  const { isFavorited } = useProductFavoriteV2();
  const { data: priceData } = usePDPPrice(null, null, {});

  const {
    loading: basicDetailsLoading,
    data: basicDetails,
  } = usePDPBasicDetails();

  const { loading: optionLoading, data: optionData } = usePDPOptions();

  const pdpLastSelectedOptionId = usePdpLastSelectedOptionId();
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const { data: pdpClearanceData } = usePDPClearanceData();
  // state
  const [isLoaded, setIsLoaded] = useState(false);

  const { isClearance } = priceData || {};
  const isDecideLater = pdpSelectedCustomizerTab === 'decide-later';

  const [addToRecentlyViewedAPI] = useMutation(ADD_TO_RECENTLY_VIEWED);
  const urlParams = useParams();

  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  const variantSkuFlashSale = !!usePDPContext()?.productOptions?.data?.stock
    ?.variantSkuFlashSale?.[selectedOptionsVariantSku];
  const isFlashSale =
    pdpClearanceData?.optionsClearanceData?.[selectedOptionsVariantSku]
      ?.flashSale === '1' || false;

  // view PDP event
  useEffect(() => {
    if (
      !isLoaded &&
      !basicDetailsLoading &&
      !optionLoading &&
      basicDetails?.slug &&
      urlParams.slug === basicDetails?.slug &&
      optionData.options &&
      priceData?.price?.final_price
    ) {
      setIsLoaded(true);
      viewPDPEvent({
        isClearance,
        isDecideLater,
        isFavorited,
        isFlashSale: variantSkuFlashSale,
        isQuickShip: optionData?.stock?.isSelectedOptionsQuickship,
        optionPrice: priceData?.optionPrice,
        options: optionData.options,
        price: priceData?.price,
        product: basicDetails,
        saveRecentlyViewed: addToRecentlyViewedAPI,
        selectedOptions: optionData.selectedOptions,
        sku: selectedOptionsVariantSku,
        userEmail,
        userId,
      });
    }
    if (isLoaded) {
      setIsLoaded(false);
    }
    // TODO: Once available, we should update this to to use the useEvent hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    basicDetailsLoading,
    optionLoading,
    basicDetails?.slug,
    optionData.options,
    priceData?.price?.final_price,
    isFlashSale,
  ]);

  // select variation event
  useEffect(() => {
    if (
      !basicDetailsLoading &&
      !optionLoading &&
      urlParams.slug === basicDetails?.slug &&
      priceData?.price?.final_price
    ) {
      selectVariantAnalyticsEvent({
        pdpLastSelectedOptionId,
        selectedOptions: optionData.selectedOptions,
        options: optionData.options,
        sku: selectedOptionsVariantSku,
        price: priceData?.price?.final_price,
        optionPrice: priceData?.optionPrice,
        product: basicDetails,
        isDecideLater,
      });
    }
    // TODO: Once available, we should update this to to use the useEvent hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicDetailsLoading, optionLoading]);
};

export default usePDPBasicEvents;
